/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/index.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { ArrowRight, Bot, Calendar, CheckCircle, DollarSign, MessageSquare, Network, Rocket, Target, Users, Workflow } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import Motion, { fadeIn } from '@/client/components/Motion';
import PublicFooter from '@/client/components/marketing/PublicFooter';
import PublicHeader from '@/client/components/marketing/PublicHeader';
import Page from '@/client/pages/_layout/_page';
import { MARKETING_URLS } from '@/client/catalogs/marketing/navigation';

/*----------------------------------
- TYPES
----------------------------------*/

interface FeatureCard {
  icon: typeof Network;
  title: string;
  description: string;
}
interface BenefitCard extends FeatureCard {
  stat: string;
}
interface TestimonialCard {
  name: string;
  role: string;
  company: string;
  quote: string;
  stat: string;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const HERO_CARDS: FeatureCard[] = [{
  icon: Network,
  title: 'Network Power',
  description: 'Leverage your contacts'
}, {
  icon: DollarSign,
  title: 'Auto Commissions',
  description: 'Automated rewards'
}, {
  icon: Rocket,
  title: 'Unified Inbox',
  description: 'LinkedIn, Email, WhatsApp'
}, {
  icon: Bot,
  title: 'AI Assistant',
  description: 'Meeting prep and follow-ups'
}];
const PROBLEM_CARDS: BenefitCard[] = [{
  icon: Network,
  title: 'Turn Connections Into Partners',
  description: 'Every connection becomes a potential referral source. Track recommendations and reward them automatically.',
  stat: '3x more referrals'
}, {
  icon: DollarSign,
  title: 'Automated Commission Management',
  description: 'No more spreadsheets. CrossPath tracks every referral and calculates commissions automatically.',
  stat: 'Zero manual tracking'
}, {
  icon: MessageSquare,
  title: 'Centralized Communication',
  description: 'LinkedIn, Email, and WhatsApp in one unified inbox. Never switch tools again.',
  stat: '5+ hours saved daily'
}];
const SOLUTION_CARDS: FeatureCard[] = [{
  icon: Network,
  title: 'Network-Powered Recruiting',
  description: 'Identify referral partners, track candidate recommendations, and automatically manage commissions for every successful placement.'
}, {
  icon: Workflow,
  title: 'Automated Campaign and CRM',
  description: 'Set up multi-channel campaigns with automatic follow-ups. CrossPath tracks every interaction across LinkedIn, Email, and WhatsApp.'
}, {
  icon: Bot,
  title: 'AI Multi-Task Assistant',
  description: 'Prepare meeting briefs, generate follow-up emails, create summaries, and manage your calendar automatically.'
}];
const FEATURE_GRID: FeatureCard[] = [{
  icon: Network,
  title: 'Referral Partner Network',
  description: 'Transform candidates into active referral sources with automated tracking.'
}, {
  icon: DollarSign,
  title: 'Commission Automation',
  description: 'Automatic calculation and tracking of referral commissions.'
}, {
  icon: Workflow,
  title: 'Multi-Channel Campaigns',
  description: 'LinkedIn, Email, and WhatsApp campaigns with smart follow-ups.'
}, {
  icon: MessageSquare,
  title: 'Unified Communication Hub',
  description: 'All channels in one inbox so your team never switches tools.'
}, {
  icon: Bot,
  title: 'AI Meeting Assistant',
  description: 'Auto-generate briefs, summaries, and follow-up emails.'
}, {
  icon: Calendar,
  title: 'Smart Calendar Management',
  description: 'AI schedules and prepares you for every meeting.'
}];
const AUDIENCE_SEGMENTS = [{
  title: 'Individual',
  description: 'For freelancers and solo recruiters',
  body: 'Compete at scale without the overhead. Everything you need to automate outreach and manage clients.',
  href: MARKETING_URLS.saasDemo,
  linkClassName: 'text-orange-600 hover:text-pink-600',
  cardClassName: 'border-orange-200 hover:border-orange-400'
}, {
  title: 'Professional',
  description: 'For agencies from 10 to 80 people',
  body: 'Powerful collaboration, advanced analytics, and team management. Scale your recruitment operations.',
  href: MARKETING_URLS.saasDemo,
  linkClassName: 'text-pink-600 hover:text-purple-600',
  cardClassName: 'border-pink-200 hover:border-pink-400'
}];
const WORKFLOW_STEPS = [{
  icon: Target,
  step: '01',
  title: 'Identify hiring companies',
  description: 'Our AI scans thousands of sources to find companies actively hiring in your niche.'
}, {
  icon: Users,
  step: '02',
  title: 'Engage companies and candidates with automation',
  description: 'Personalized outreach at scale. AI writes messages that sound like you, not a robot.'
}, {
  icon: CheckCircle,
  step: '03',
  title: 'Collaborate and deliver results',
  description: 'Track progress, share insights with your team, and close more placements faster.'
}];
const TESTIMONIALS: TestimonialCard[] = [{
  name: 'Sarah Johnson',
  role: 'Senior Recruiter',
  company: 'TechRecruit Pro',
  quote: 'CrossPath cut my sourcing time in half. The AI personalization is scary good.',
  stat: '50% faster sourcing'
}, {
  name: 'Michael Chen',
  role: 'Agency Owner',
  company: 'Elite Talent Group',
  quote: 'Finally, a platform that my whole team actually wants to use every day.',
  stat: '10 team members'
}, {
  name: 'Emma Williams',
  role: 'Freelance Recruiter',
  company: 'Independent',
  quote: 'I can now compete with big agencies. This is a game-changer for solo recruiters.',
  stat: '3x more placements'
}];

/*----------------------------------
- CONTROLEUR
----------------------------------*/

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/', { ...({
  static: {
    refresh: '1h',
    urls: ['/']
  }
}), ...{ filepath: "/app/client/pages/index.tsx", sourceLocation: { line: 168, column: 1 }, id: "index" } }, null, ({}) => {
  return <Page withLayout={false}>
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50">
                <PublicHeader currentPage="saas" ctaLabel="Request a Demo" ctaHref={MARKETING_URLS.saasDemo} />

                <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-white to-pink-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <Motion tag="div" initial="hidden" animate="visible" variants={fadeIn} transition={{
              duration: 0.6
            }}>
                                <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 leading-tight">
                                    Turn Your Network Into Revenue
                                </h1>
                                <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                                    The recruitment platform that transforms candidates and contacts into referral partners. Centralize communication, automate campaigns, and manage commissions automatically.
                                </p>
                                <div className="flex justify-center lg:justify-start">
                                    <a href={MARKETING_URLS.saasDemo} className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-10 py-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 hover:scale-105 transform">
                                        Request a Demo
                                        <ArrowRight size={20} />
                                    </a>
                                </div>
                            </Motion>

                            <Motion tag="div" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative">
                                <div className="bg-white rounded-2xl shadow-2xl p-6 border border-orange-100">
                                    <div className="bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100 rounded-xl p-8">
                                        <div className="grid grid-cols-2 gap-4">
                                            {HERO_CARDS.map((card, index) => <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center mb-3">
                                                        <card.icon className="w-6 h-6 text-white" />
                                                    </div>
                                                    <h4 className="font-bold text-slate-900 mb-1">{card.title}</h4>
                                                    <p className="text-xs text-slate-600">{card.description}</p>
                                                </div>)}
                                        </div>
                                    </div>
                                </div>
                            </Motion>
                        </div>
                    </div>
                </section>

                <section id="about" className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-orange-50">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-4">
                                Why Top Recruiters Choose CrossPath
                            </h2>
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {PROBLEM_CARDS.map((item, index) => <Motion tag="div" key={index} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-8 border-2 border-orange-100 hover:border-orange-300 hover:shadow-xl transition-all group">
                                    <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                        <item.icon className="w-7 h-7 text-orange-600" />
                                    </div>
                                    <div className="mb-3">
                                        <span className="inline-block bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                                            {item.stat}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                    <p className="text-slate-700 leading-relaxed">{item.description}</p>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-purple-50 to-orange-50">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-4">
                                The Complete Recruitment Stack
                            </h2>
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {SOLUTION_CARDS.map((item, index) => <Motion tag="div" key={index} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-8 border-2 border-pink-100 hover:border-pink-300 hover:shadow-2xl transition-all hover:-translate-y-2">
                                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-pink-500 to-purple-500 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                                        <item.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                                    <p className="text-slate-700 leading-relaxed">{item.description}</p>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section id="features" className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-pink-50">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-4">
                                More than an ATS, your intelligent assistant.
                            </h2>
                            <p className="text-slate-600 text-lg">Everything you need to recruit smarter, not harder</p>
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                            {FEATURE_GRID.map((feature, index) => <Motion tag="div" key={index} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.05
            }} className="bg-white rounded-2xl p-6 border-2 border-orange-100 hover:border-orange-300 hover:shadow-xl hover:scale-105 transition-all">
                                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-pink-100 rounded-lg flex items-center justify-center mb-4">
                                        <feature.icon className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                                    <p className="text-slate-700 text-sm leading-relaxed">{feature.description}</p>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-purple-50">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                                Designed for every kind of recruiter.
                            </h2>
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {AUDIENCE_SEGMENTS.map((segment, index) => <Motion tag="div" key={segment.title} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className={`bg-white rounded-2xl p-10 border-2 hover:shadow-2xl transition-all ${segment.cardClassName}`}>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{segment.title}</h3>
                                    <p className="text-sm text-slate-500 mb-4">{segment.description}</p>
                                    <p className="text-slate-700 mb-6 leading-relaxed">{segment.body}</p>
                                    <a href={segment.href} className={`font-semibold inline-flex items-center gap-2 ${segment.linkClassName}`}>
                                        Request a demo <ArrowRight size={18} />
                                    </a>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section id="how-it-works" className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white via-orange-50 to-pink-50">
                    <div className="max-w-4xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-4">
                                How it works in 3 simple steps.
                            </h2>
                            <p className="text-slate-600 text-lg">Get started in minutes, see results in days</p>
                        </Motion>

                        <div className="space-y-12">
                            {WORKFLOW_STEPS.map((item, index) => <Motion tag="div" key={index} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className="flex gap-6 items-start">
                                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <item.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-orange-600 font-bold text-sm mb-2">{item.step}</div>
                                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{item.title}</h3>
                                        <p className="text-slate-700 leading-relaxed">{item.description}</p>
                                    </div>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
                    <div className="max-w-6xl mx-auto">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 to-orange-600 bg-clip-text text-transparent mb-4">
                                Loved by recruiters everywhere.
                            </h2>
                            <p className="text-slate-600 text-lg">See what our users are saying</p>
                        </Motion>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {TESTIMONIALS.map((testimonial, index) => <Motion tag="div" key={testimonial.name} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeIn} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-8 border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all relative">
                                    <div className="absolute -top-3 -right-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                        {testimonial.stat}
                                    </div>
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                            {testimonial.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-slate-900">{testimonial.name}</div>
                                            <div className="text-sm text-slate-600">{testimonial.role}</div>
                                            <div className="text-xs text-slate-500">{testimonial.company}</div>
                                        </div>
                                    </div>
                                    <p className="text-slate-700 leading-relaxed italic">"{testimonial.quote}"</p>
                                </Motion>)}
                        </div>
                    </div>
                </section>

                <section className="py-24 px-6 lg:px-8 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600">
                    <div className="max-w-4xl mx-auto text-center">
                        <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn}>
                            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                                Ready to 10x your recruitment efficiency?
                            </h2>
                            <p className="text-xl text-white/90 mb-8">Join hundreds of recruiters already using CrossPath</p>
                            <div className="flex justify-center">
                                <a href={MARKETING_URLS.saasDemo} className="bg-white text-orange-600 hover:bg-orange-50 px-10 py-4 rounded-xl font-semibold text-lg transition-all shadow-2xl hover:shadow-2xl hover:scale-105 transform flex items-center gap-2">
                                    Get Started
                                    <ArrowRight size={20} />
                                </a>
                            </div>
                        </Motion>
                    </div>
                </section>

                <PublicFooter />
            </div>
        </Page>;
});
};
